import { Component, OnInit } from '@angular/core';
import { Client } from '../../../models/client';
import { ActivatedRoute } from '@angular/router';
import { ApiService, QueryParam } from '../../../services/api.services';
import { ClientDetailsService } from '../../../services/client-details.service';
import { SystemCapabilityService } from '../../../services/system-capabilities/system-capability.service';
import { SystemCapabilityEnum } from '../../../models/data/systemCapability';
import { UserProfileService } from '../../../services/user-profile.service';
import { AppTabNavigationHeaderComponent } from '../../../components/navigation-header/app-navigation-header.component';
import { MatTabGroup, MatTab, MatTabContent } from '@angular/material/tabs';
import { QueryStringTabGroupDirective } from '../../../directives/query-string-tab-group.directive';
import { ClientAboutComponent } from '../client-about/client-about.component';
import { BreadcrumbLabelDirective } from '../../../directives/breadcrumb-label.directive';
import { PropertyListTableComponent } from '../../property/property-list-table/property-list-table.component';
import { AllDevicesComponent } from '../../device/main/all-devices/all-devices.component';
import { AuditLogListComponent } from '../../audit-log-list/audit-log-list.component';

@Component({
	selector: 'app-client-about-main-tab',
	templateUrl: './client-about-main-tab.component.html',
	styleUrl: './client-about-main-tab.component.scss',
	imports: [
		AppTabNavigationHeaderComponent,
		MatTabGroup,
		QueryStringTabGroupDirective,
		MatTab,
		MatTabContent,
		ClientAboutComponent,
		BreadcrumbLabelDirective,
		PropertyListTableComponent,
		AllDevicesComponent,
		AuditLogListComponent,
	],
})
export class ClientAboutMainTabComponent implements OnInit {
	clientId: string = this.route.snapshot.params.id;
	client?: Client;
	clientName = $localize`:@@clientMainTab-breadcrumbLabel:Clients`;
	propertyBreadcrumbLabel = '';
	devicesBreadcrumbLabel = '';
	auditLogBreadcrumbLabel = '';

	canAccessAuditLogs = false;
	activeCompanyId: string | undefined;
	isClientLoaded = false;

	/** Audit log columns to hide. Empty = show all (date/time, performed by, from company, user, company, device, device group, property, details). */
	hiddenAuditLogColumns: string[] = [];

	constructor(
		private readonly route: ActivatedRoute,
		private readonly apiService: ApiService,
		private readonly clientDetailsService: ClientDetailsService,
		private readonly systemCapabilityService: SystemCapabilityService,
		private readonly userProfileService: UserProfileService
	) {}

	async ngOnInit(): Promise<void> {
		await this.getInitialData();
	}

	async getInitialData(): Promise<void> {
		const [clientDetails, userProfile] = await Promise.all([
			this.clientDetailsService.getClientDetails(this.clientId),
			this.userProfileService.getUserProfile(),
		]);
		this.client = clientDetails;
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id;
		this.isClientLoaded = !!this.client;

		this.clientName = this.client?.firstName + ' ' + this.client?.lastName;

		this.propertyBreadcrumbLabel = this.clientName;
		this.devicesBreadcrumbLabel = this.clientName;
		this.auditLogBreadcrumbLabel = this.clientName;

		// Show audit logs tab only for companies with audit log capability that own the client (device owner)
		const hasAuditLogsCapability = this.systemCapabilityService.has(
			SystemCapabilityEnum.auditLogs
		);
		const companyOwnsClient = this.client.companyId === this.activeCompanyId;
		this.canAccessAuditLogs = hasAuditLogsCapability && companyOwnsClient;
	}

	// Callback to fetch property data for this client
	readonly clientPropertyDataRequest = async (_filter: QueryParam[]) => {
		if (!this.clientId) {
			return [];
		}

		try {
			return await this.apiService.getClientProperty(this.clientId);
		} catch (error) {
			return [];
		}
	};

	// Callback to fetch device data for this client's property
	readonly filteredDevicesByClientProperty = async (filter: QueryParam[]) => {
		if (!this.clientId) {
			return [];
		}

		try {
			return await this.apiService.getClientDevices(this.clientId, filter);
		} catch (error) {
			return [];
		}
	};
}
